import React, { useState, useEffect, Suspense, useCallback } from 'react';
import { isCSMHelpCenter } from '@helpCenter/util/advanced-help-center';
import ErrorBoundary from '@helpCenter/view/error-boundary/error-boundary';
import {
    isMoveToManagementRouterEnabled,
    isProjectMappingInHelpCenterEnabled,
    isCustomPagesUiEnabled,
} from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useLazyLoadQuery } from 'react-relay';
import { useRouterActions, usePathParam, useRouter } from 'react-resource-router';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { SpotlightTarget, SpotlightPulse } from '@atlaskit/onboarding';
import { LeftSidebarWithoutResize } from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import {
    GoBackItem,
    ButtonItem,
    HeadingItem,
    Section,
    SideNavigation,
    NavigationHeader,
    NavigationContent,
} from '@atlaskit/side-navigation';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ChangeBoardingSingleSpotlight } from '@atlassian/help-center-common-component/change-boarding-single-spotlight';
import {
    EngagementMessages,
    EngagementMessagesTarget,
    HELP_CENTERS_MANAGEMENT_BASE_PATH,
} from '@atlassian/help-center-common-component/constants';
import { EngagementCoordinationClient } from '@atlassian/help-center-common-component/engagement-coordination-client';
import FeedbackCollectorButton from '@atlassian/help-center-common-component/feedback-collector/feedback-collector-button';
import { getEnv, getIsHCCustomizationEnabled } from '@atlassian/help-center-common-util/env';
import { getWindow } from '@atlassian/help-center-common-util/get-window';
import adminLayoutMessages from '../admin-management-layout/messages';
import messages from './messages';
import { SidebarHelpCenterHeader } from './sidebar-help-center-header';
import { SidebarSkeleton } from './sidebar-skeleton';
export interface NavigationContentItem {
    onClick: () => void;
    iconBefore?: React.ReactElement;
    text: string;
    isVisible: boolean;
    isSelected?: boolean;
    showSpotlight?: boolean;
}

interface SidebarProps {
    width: number;
    navigationContentItems: NavigationContentItem[];
    featureName: string;
}

const Sidebar: React.FC<SidebarProps> = ({ width, navigationContentItems, featureName }) => {
    di(
        isProjectMappingInHelpCenterEnabled,
        getEnv,
        useLazyLoadQuery,
        EngagementCoordinationClient,
        isCustomPagesUiEnabled,
        getIsHCCustomizationEnabled,
        isCSMHelpCenter,
        getWindow
    );
    const [isSpotlightActive, setIsSpotlightActive] = useState(false);
    const [showSpotlightpulse, setShowSpotlightpulse] = useState(false);
    const { push } = useRouterActions();
    const { formatMessage } = useIntl();
    const [{ route }] = useRouter();
    const { name: routeName } = route;
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [helpCenterId = ''] = usePathParam('helpcenterId');
    const isLandingPagesChangeboardingEnabled = FeatureGates.checkGate('help_center_landing_pages_changeboarding');
    const mapPagesScreenUrl = isMoveToManagementRouterEnabled() ? `/${helpCenterId}/pages` : '/manage-pages';
    const onBackButtonClick = useCallback(() => {
        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'click',
            actionSubjectId: `SidebarBackButton`,
        }).fire();
        if (!isProjectMappingInHelpCenterEnabled()) {
            getWindow().location.href = `${getEnv().baseUrl}`;
        } else if (isProjectMappingInHelpCenterEnabled() && isMoveToManagementRouterEnabled()) {
            push('/') as void;
        } else {
            getWindow().location.href = HELP_CENTERS_MANAGEMENT_BASE_PATH;
        }
    }, [createAnalyticsEvent, push]);

    const ActivateSpotlightPulse = () => {
        useEffect(() => {
            setShowSpotlightpulse(true);
        }, []);
        return <></>;
    };
    return (
        <LeftSidebarWithoutResize width={width}>
            <Box xcss={sidebarContainerStyles}>
                <SideNavigation label={formatMessage(messages.sidebarNavigationLabel)}>
                    <Suspense fallback={<SidebarSkeleton />}>
                        <NavigationHeader>
                            <ErrorBoundary>
                                <SidebarHelpCenterHeader />
                            </ErrorBoundary>
                        </NavigationHeader>
                        <Box padding="space.100">
                            <GoBackItem onClick={onBackButtonClick}>
                                {isProjectMappingInHelpCenterEnabled()
                                    ? formatMessage(messages.backButton)
                                    : formatMessage(messages.backButtonWithoutProjectMapping)}
                            </GoBackItem>
                        </Box>
                        <NavigationContent showTopScrollIndicator>
                            <>
                                {isProjectMappingInHelpCenterEnabled() && (
                                    <HeadingItem>{formatMessage(messages.settingsLabel)}</HeadingItem>
                                )}
                            </>
                            <Section isList>
                                {navigationContentItems.map(
                                    ({ onClick, iconBefore, text, isSelected, isVisible, showSpotlight }) => {
                                        const renderButtonItem = () => (
                                            <ButtonItem
                                                onClick={onClick}
                                                iconBefore={iconBefore}
                                                key={text}
                                                isSelected={isSelected}
                                            >
                                                {text}
                                            </ButtonItem>
                                        );

                                        if (isVisible && showSpotlight) {
                                            return (
                                                <SpotlightTarget
                                                    name={
                                                        EngagementMessagesTarget.LANDING_PAGES_ADMIN_SIDEBAR_CHANGEBOARDING_SPOTLIGHT
                                                    }
                                                    key={text}
                                                >
                                                    <SpotlightPulse radius={3} pulse={showSpotlightpulse}>
                                                        <ButtonItem
                                                            onClick={() => {
                                                                showSpotlightpulse
                                                                    ? setIsSpotlightActive(true)
                                                                    : onClick();
                                                            }}
                                                            iconBefore={iconBefore}
                                                            key={text}
                                                            isSelected={isSelected}
                                                        >
                                                            {text}
                                                        </ButtonItem>
                                                    </SpotlightPulse>
                                                </SpotlightTarget>
                                            );
                                        }
                                        return isVisible && renderButtonItem();
                                    }
                                )}
                            </Section>
                        </NavigationContent>
                        <Box xcss={navFooter}>
                            <NavigationContent showTopScrollIndicator>
                                <Section>
                                    <FeedbackCollectorButton
                                        showIcon
                                        showTypeField
                                        featureName={featureName}
                                        buttonLabel={messages.feedbackWidget}
                                        analyticsSubjectId="SidebarFeedbackButton"
                                        useSideNavButton
                                    />
                                </Section>
                            </NavigationContent>
                        </Box>
                    </Suspense>
                </SideNavigation>
            </Box>

            {!isCSMHelpCenter(getEnv().helpCenterType) &&
                isCustomPagesUiEnabled() &&
                getIsHCCustomizationEnabled() &&
                isLandingPagesChangeboardingEnabled &&
                !isSpotlightActive && (
                    <EngagementCoordinationClient messageId={EngagementMessages.LANDING_PAGES_CHANGEBOARDING}>
                        <ActivateSpotlightPulse />
                    </EngagementCoordinationClient>
                )}
            {!isCSMHelpCenter(getEnv().helpCenterType) &&
                isCustomPagesUiEnabled() &&
                getIsHCCustomizationEnabled() &&
                isLandingPagesChangeboardingEnabled &&
                isSpotlightActive && (
                    <ChangeBoardingSingleSpotlight
                        target={EngagementMessagesTarget.LANDING_PAGES_ADMIN_SIDEBAR_CHANGEBOARDING_SPOTLIGHT}
                        analyticsSubject="landingPagesAdminSidebarChangeBoarding"
                        messageId={EngagementMessages.LANDING_PAGES_CHANGEBOARDING}
                        headingText={formatMessage(adminLayoutMessages.landingPagesSidebarChangeBoardingHeading)}
                        contentText={formatMessage(adminLayoutMessages.landingPagesSidebarChangeBoardingContent)}
                        buttonText={formatMessage(adminLayoutMessages.landingPagesSidebarChangeBoardingTryButton)}
                        secondaryButtonText={formatMessage(
                            adminLayoutMessages.landingPagesSidebarChangeBoardingDismissButton
                        )}
                        onHideSpotlight={() => {
                            setShowSpotlightpulse(false);
                            routeName !== 'manage-pages' && (push(mapPagesScreenUrl) as void);
                        }}
                        onSecondaryButtonClick={() => {
                            setShowSpotlightpulse(false);
                        }}
                        dialogPlacement="right top"
                        targetBgColor={token('elevation.surface.raised', colors.N0)}
                        targetRadius={3}
                    />
                )}
        </LeftSidebarWithoutResize>
    );
};

const navFooter = xcss({
    position: 'relative',
});

const sidebarContainerStyles = xcss({
    width: '100%',
    height: '100%',
    borderRight: '1px solid',
    borderColor: 'color.border',
    boxSizing: 'inherit',
});

export default withAnalyticsContext({ component: 'SideBar' })(Sidebar);
